import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button, Eyebrow } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check, ArrowRight } from 'lucide-react'
import { IntegrationStatusChip } from '@/components/IntegrationStatusChip'
import { TIER_COPY, integrationsWithPages } from '@/data/integrations'
import type { Integration } from '@/data/integrations'

export function IntegrationPageTemplate({ integration }: { integration: Integration }) {
  const detail = integration.detail
  if (!detail) throw new Error(`Integration ${integration.slug} has no detail content`)

  const url = `/integrations/${integration.slug}`
  const breadcrumbs = generateBreadcrumbs(url, breadcrumbLabels)

  useSEO({
    title: detail.seoTitle,
    description: detail.seoDescription,
    url,
    canonical: url,
    breadcrumbs,
    schema: createServiceSchema({
      name: detail.seoTitle,
      description: detail.seoDescription,
      url,
    }),
  })

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const capsRef = useStaggeredGrid<HTMLElement>('.cap-item', { stagger: 0.08 })
  const limitsRef = useScrollReveal<HTMLElement>('.limits-content')
  const stepsRef = useStaggeredGrid<HTMLDivElement>('.step-item', { stagger: 0.12 })
  const faqRef = useScrollReveal<HTMLElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLElement>('.cta-content')

  const others = integrationsWithPages.filter((i) => i.slug !== integration.slug)

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Eyebrow>{TIER_COPY[integration.tier].label} integration</Eyebrow>
          <IntegrationStatusChip status={integration.status} />
        </div>
        <h1 className="text-fluid-h1">{detail.heroHeadline}</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">{detail.heroBody}</p>
        {integration.caveat && (
          <p className="text-muted-foreground/80 mx-auto max-w-2xl text-sm italic">{integration.caveat}</p>
        )}
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call <ArrowRight className="ml-1 h-5 w-5" />
              </Button>
            </m.div>
          </a>
        </div>
      </section>

      {/* The short version */}
      <section className="mx-auto max-w-3xl space-y-4">
        <h2 className="text-fluid-h3">The short version</h2>
        <p className="text-muted-foreground text-lg leading-relaxed">{detail.shortVersion}</p>
      </section>

      {/* What you get */}
      <section ref={capsRef} className="space-y-6">
        <h2 className="text-fluid-h2 text-center">What you get</h2>
        <ul className="mx-auto grid max-w-4xl gap-4 md:grid-cols-2">
          {detail.capabilities.map((capability) => (
            <li key={capability} className="cap-item bg-card border-border flex gap-3 rounded-xl border p-5">
              <Check className="text-primary mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="text-muted-foreground text-sm leading-relaxed">{capability}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* What it does not do — every detail page has one */}
      <section ref={limitsRef} className="limits-content mx-auto max-w-3xl space-y-4">
        <h2 className="text-fluid-h3">What it does not do</h2>
        <p className="text-muted-foreground text-sm">
          Worth knowing before you plan around it. We would rather you find this here than after signing.
        </p>
        <ul className="space-y-3">
          {detail.limitations.map((limitation) => (
            <li
              key={limitation}
              className="border-border/60 text-muted-foreground border-l-2 py-1 pl-4 leading-relaxed"
            >
              {limitation}
            </li>
          ))}
        </ul>
      </section>

      {/* Setup */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">How setup works</h2>
        <div ref={stepsRef} className="mx-auto grid max-w-4xl gap-6 md:grid-cols-3">
          {detail.setupSteps.map((step, index) => (
            <div key={step.title} className="step-item bg-card border-border space-y-3 rounded-2xl border p-6">
              <span className="bg-primary/10 text-primary flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold">
                {index + 1}
              </span>
              <h3 className="font-bold">{step.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section ref={faqRef} className="mx-auto max-w-3xl space-y-6">
        <h2 className="text-fluid-h2 text-center">Questions</h2>
        <div className="space-y-4">
          {detail.faqs.map((faq) => (
            <div key={faq.question} className="faq-item bg-card border-border space-y-2 rounded-xl border p-6">
              <h3 className="font-bold">{faq.question}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Other integrations */}
      {others.length > 0 && (
        <section className="space-y-6">
          <h2 className="text-fluid-h3 text-center">Other integrations</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((other) => (
              <Link
                key={other.slug}
                to={`/integrations/${other.slug}`}
                className="border-border/50 hover:border-primary/30 group rounded-xl border p-5 transition-all duration-300 hover:shadow-md"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold">{other.name}</span>
                  <IntegrationStatusChip status={other.status} />
                </div>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{other.summary}</p>
              </Link>
            ))}
          </div>
          <p className="text-center">
            <Link to="/integrations" className="text-primary text-sm font-medium">
              See every integration and where it stands &rarr;
            </Link>
          </p>
        </section>
      )}

      {/* CTA */}
      <section
        ref={ctaRef}
        className="cta-content from-primary/10 via-primary/5 border-primary/20 space-y-6 rounded-2xl border bg-linear-to-br to-transparent p-8 text-center md:p-12"
      >
        <h2 className="text-fluid-h3">Not sure this fits how you work?</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          Bring what you run today to a call. You get a straight answer on whether connecting it is worth it, including
          when the answer is no.
        </p>
        <a href="/book" target="_blank" rel="noopener noreferrer">
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className="inline-block"
          >
            <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Book a Free Call <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
          </m.div>
        </a>
      </section>

      <FugueContactForm />
    </div>
  )
}
